import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  pushSheet, popSheet, isTopSheet, suppressNextPop, isPopSuppressed,
} from '../sheetStack.js'

const ANIM_MS = 200

export function BottomSheet({ open, onClose, children, className = '' }) {
  const [mounted, setMounted] = useState(false)
  const [entered, setEntered] = useState(false)

  useEffect(() => {
    if (open) {
      setMounted(true)
      return
    }
    if (mounted) {
      const t = setTimeout(() => setMounted(false), ANIM_MS)
      return () => clearTimeout(t)
    }
  }, [open, mounted])

  const sheetIdRef = useRef(null)
  useEffect(() => {
    if (!mounted) return
    sheetIdRef.current = pushSheet()
    return () => popSheet(sheetIdRef.current)
  }, [mounted])

  useLayoutEffect(() => {
    if (open && mounted) {
      const id = requestAnimationFrame(() => setEntered(true))
      return () => cancelAnimationFrame(id)
    }
    setEntered(false)
  }, [open, mounted])

  const onCloseRef = useRef(onClose)
  useEffect(() => { onCloseRef.current = onClose }, [onClose])

  // Esc closes (only the topmost sheet — Esc bubbles once and the most
  // recently mounted listener fires first thanks to useEffect ordering).
  useEffect(() => {
    if (!open) return
    function onKey(e) { if (e.key === 'Escape') { e.stopPropagation(); onCloseRef.current() } }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  // Browser back closes the sheet instead of navigating away.
  // Con sheet annidati: reagisce solo lo sheet in cima, e il back()
  // programmatico della chiusura non deve chiudere a cascata quelli sotto.
  const closedByPopRef = useRef(false)
  useEffect(() => {
    if (!open) return
    history.pushState({ sheet: true }, '')
    closedByPopRef.current = false
    function onPop() {
      if (isPopSuppressed()) return
      if (!isTopSheet(sheetIdRef.current)) return
      closedByPopRef.current = true
      onCloseRef.current()
    }
    window.addEventListener('popstate', onPop)
    return () => {
      window.removeEventListener('popstate', onPop)
      if (!closedByPopRef.current && history.state && history.state.sheet) {
        suppressNextPop()
        history.back()
      }
    }
  }, [open])

  if (!mounted) return null

  // Outside click: only when clicking the dim overlay itself (not children).
  // This is naturally "topmost only": the inner sheet of a nested select
  // overlays the outer form, so clicking outside the inner overlay still
  // counts as inside the outer form (which has its own overlay further out).
  function onOverlayClick(e) {
    if (e.target === e.currentTarget) onCloseRef.current()
  }

  return createPortal(
    <div
      className={`bsheet${entered ? ' bsheet-entered' : ''} ${className}`}
      onMouseDown={onOverlayClick}
    >
      {children}
    </div>,
    document.body,
  )
}
